import { Reducer } from 'redux';
import { EditState, ITranslationEditItem } from '../models';
import { getTranslations, ITranslationApiParameters, saveTranslations } from '../services/TranslationsAPI';
import {
	ITranslationEditorSaveFailed,
	ITranslationEditorSaveRequested,
	ITranslationEditorSaveSucceeded,
	ITranslationEditorUpdated,
	ITranslationsGetFailed,
	ITranslationsGetRequested,
	ITranslationsGetSucceeded,
} from './Actions';
import { ActionTypes } from './ActionTypes';
import { TranslationHelper } from '../utilities';
import { AppDispatch } from '../appStore';
import { IApplicationState } from './';

export interface ITranslationEditorState {
	instances: { [key: string]: ITranslationEditorInstance };
}
export const defaultState: ITranslationEditorState = {
	instances: {},
};

export interface ITranslationEditorInstance {
	loading: boolean;
	defaultCultureCode: string;
	translations: ITranslationEditItem[];
	lastSavedTranslations: ITranslationEditItem[];
}
export const defaultInstanceState: ITranslationEditorInstance = {
	loading: false,
	defaultCultureCode: 'en-US',
	translations: [],
	lastSavedTranslations: [],
};
export const defaultTranslation: ITranslationEditItem = {
	cultureCode: '',
	language: '',
	textValue: '--',
	editState: EditState.None,
};

export type KnownActions =
	| ITranslationEditorUpdated
	| ITranslationEditorSaveRequested
	| ITranslationEditorSaveSucceeded
	| ITranslationEditorSaveFailed
	| ITranslationsGetRequested
	| ITranslationsGetSucceeded
	| ITranslationsGetFailed;

export const ActionCreators = {
	saveTranslations:
		(key: string, apiParams: ITranslationApiParameters, translations: ITranslationEditItem[]) =>
		(dispatch: AppDispatch, getState: () => IApplicationState) => {
			dispatch({ type: ActionTypes.TRANSLATION_EDITOR_SAVE_REQUESTED, payload: { key } });
			dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

			const defaultCultureCode = getState().survey.surveyEditModel.defaultCultureCode;
			return saveTranslations(apiParams, defaultCultureCode, translations)
				.then((savedTranslations: ITranslationEditItem[]) => {
					dispatch({
						type: ActionTypes.TRANSLATION_EDITOR_SAVE_SUCCEEDED,
						payload: { key, translations: savedTranslations, defaultCultureCode },
					});
					dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
				})
				.catch((error: Error) => {
					dispatch({ type: ActionTypes.TRANSLATION_EDITOR_SAVE_FAILED, payload: { key } });
					dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: error.message });
				});
		},
	updateTranslations: (key: string, translations: ITranslationEditItem[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.TRANSLATION_EDITOR_UPDATED, payload: { key, translations } });
	},
	getTranslations: (key: string, apiParams: ITranslationApiParameters) => (dispatch: AppDispatch, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.TRANSLATIONS_GET_REQUESTED, payload: { key } });

		const defaultCultureCode = getState().survey.surveyEditModel.defaultCultureCode;
		return getTranslations(apiParams)
			.then((translations: ITranslationEditItem[]) =>
				dispatch({ type: ActionTypes.TRANSLATIONS_GET_SUCCEEDED, payload: { key, translations, defaultCultureCode } }),
			)
			.catch(() => dispatch({ type: ActionTypes.TRANSLATIONS_GET_FAILED, payload: { key } }));
	},
};

export const reducer: Reducer<ITranslationEditorState, KnownActions> = (
	state: ITranslationEditorState | undefined,
	action: KnownActions,
) => {
	if (!state) {
		state = { ...defaultState };
	}

	if (!action.payload) {
		return { ...state };
	}

	const { key } = action.payload;
	const instance = state.instances[key] || { ...defaultInstanceState };

	switch (action.type) {
		// GET
		case ActionTypes.TRANSLATION_EDITOR_SAVE_REQUESTED:
		case ActionTypes.TRANSLATIONS_GET_REQUESTED:
			return {
				...state,
				instances: {
					...state.instances,
					[key]: { ...instance, loading: true },
				},
			};
		case ActionTypes.TRANSLATIONS_GET_FAILED:
			return {
				...state,
				instances: {
					...state.instances,
					[key]: { ...instance, loading: false },
				},
			};

		// SAVE / GET SUCCEEDED
		case ActionTypes.TRANSLATION_EDITOR_SAVE_SUCCEEDED:
		case ActionTypes.TRANSLATIONS_GET_SUCCEEDED: {
			const nextInstance: ITranslationEditorInstance = {
				...instance,
				defaultCultureCode: action.payload.defaultCultureCode,
				loading: false,
				translations: [...action.payload.translations],
				lastSavedTranslations: [...action.payload.translations],
			};
			return {
				...state,
				instances: {
					...state.instances,
					[key]: nextInstance,
				},
			};
		}

		case ActionTypes.TRANSLATION_EDITOR_UPDATED: {
			const updatedTranslations = TranslationHelper.updateTranslationEditState(
				action.payload.translations,
				state.instances[key].lastSavedTranslations,
			);

			const nextInstanceState: ITranslationEditorInstance = {
				...instance,
				loading: false,
				translations: updatedTranslations,
			};

			return {
				...state,
				instances: {
					...state.instances,
					[key]: nextInstanceState,
				},
			};
		}
	}

	return state;
};
